import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useHighlight } from "../../../context";
import { usePublication } from "../../../hooks/usePublication";
import { ConfirmDeleteButton } from "../RightMenu/Notes/ConfirmDeleteButton";

const COLORS = ["#F9CE4E", "#999FD9", "#E0314E"] as const;

export interface Highlight {
  id: string;
  fragmentId: string;
  text: string;
  color: string;
  note?: string;
  startIndex: number;
  endIndex: number;
  fecha?: string;
  chapterId?: string;
}

export interface NewHighlight extends Omit<Highlight, "id"> {}

export interface PopupProps {
  selectionInfo?: {
    fragmentId: string;
    text: string;
    startIndex: number;
    endIndex: number;
    chapterId: string
  } | null;
  existingHighlight?: Highlight | null;
  onClose: () => void;
}

const PopupContent: React.FC<PopupProps> = ({
  selectionInfo,
  existingHighlight,
  onClose,
}) => {
  const { addHighlight, updateHighlight, removeHighlight } = useHighlight();
  const isEdit = !!existingHighlight;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { publication } = usePublication();

  const [activeTab, setActiveTab] = useState(0);
  const [note, setNote] = useState(existingHighlight?.note || "");
  const [color, setColor] = useState(existingHighlight?.color || COLORS[0]);

  // Guardar o crear highlight
  const handleSave = (): void => {
    const title = publication.sections?.[publication.currentSection]?.title || 'Desconocido';
    if (isEdit && existingHighlight) {
      updateHighlight(existingHighlight.id, { note, color, chapterId: title});
    } else if (selectionInfo) {
      const newHighlight: NewHighlight = {
        fragmentId: selectionInfo.fragmentId,
        text: selectionInfo.text,
        color,
        note,
        startIndex: selectionInfo.startIndex,
        endIndex: selectionInfo.endIndex,
        chapterId: title,
      };
      addHighlight(newHighlight);
    }
    onClose();
    window.getSelection()?.removeAllRanges();
  };

  const handleColorClick = (c: string) => {
    setColor(c);

    // Enfocar el textarea justo después de cambiar el color
    // Esperamos un pequeño tick para asegurar que React haya renderizado
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  // Eliminar highlight
  const handleDelete = (): void => {
    if (existingHighlight) {
      removeHighlight(existingHighlight.id);
      setTimeout(() => onClose(), 0);
    }
  };

  // Estilo centrado fijo
  const style: React.CSSProperties = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 99999,
  };

  return (
    <div data-hl-popup style={style}>
      <div
        className="sel-win"
        style={{ background: "#E6EEEF", borderRadius: "20px" }}
      >
        {/* HEADER */}
        <div className="sel-win__header">
          <div className="sel-win__header-options">
            <div className="sel-win__actions">
              {COLORS.map((c, i) => (
                <button
                  key={i}
                  type="button"
                  className={`btn btn-rounded btn-rounded--border ${
                    c === "#F9CE4E"
                      ? "btn-rounded--yellow"
                      : c === "#999FD9"
                      ? "btn-rounded--blue"
                      : c === "#E0314E"
                      ? "btn-rounded--red"
                      : "btn-rounded--null"
                  } ${color === c ? "selected" : ""}`}
                  aria-label={`Seleccionar color ${i}`}
                  onClick={() => handleColorClick(c)}
                ></button>
              ))}
            </div>

            <div className="sel-win__actions">
              <button
                type="button"
                className="btn btn-alt-primary btn-opts-a"
                data-tippy-content="Editar color de resaltado"
                onClick={() => setActiveTab(0)}
                aria-label="Editar color de resaltado y editar nota"
                style={{
                  backgroundColor: activeTab === 0 ? "#0082bc" : "#f2f6f7",
                  color: activeTab === 0 ? "#ffffff" : "#000000",
                }}
              >
                <span className="btn__icon">
                  <i className="icon icon-highlighter"></i>
                </span>
              </button>
            </div>
          </div>

          <button
            className="btn btn-rounded btn-rounded-transparent btn-close-win"
            aria-label="Cerrar panel"
            onClick={onClose}
          >
            <span className="btn__icon">
              <i className="icon icon-vpro-close"></i>
            </span>
          </button>
        </div>

        {/* BODY */}
        <div className="sel-win__body">
          {activeTab === 0 && (
            <div id="pill-0" className="sel-win__body-content active">
              <div className="sel-win__content">
                <div className="sel-win__txt-field">
                  <textarea
                    ref={textareaRef}
                    name="Nota"
                    className="txt-area"
                    cols={30}
                    rows={8}
                    placeholder="Escribe una nota"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-rounded btn-rounded-secondary"
                    onClick={() => navigator.clipboard.writeText(note)}
                  >
                    <span className="btn__icon">
                      <i className="icon icon-copy"></i>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 2 && (
            <div id="pill-2" className="sel-win__body-content active">
              <div className="sel-win__content">
                <div className="sel-win__txt-field">wiki</div>
              </div>
            </div>
          )}

          {activeTab === 3 && (
            <div id="pill-3" className="sel-win__body-content active">
              <div className="sel-win__content">
                <div className="sel-win__txt-field">traducir</div>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="sel-win__footer">
          <div className="sel-win__actions">
            {isEdit && (
              <ConfirmDeleteButton onConfirm={handleDelete} />
            )}
          </div>
          <div className="sel-win__actions">
            <button
              type="button"
              className="btn btn-alt-secondary"
              onClick={onClose}
            >
              <span className="btn__label">Cancelar</span>
            </button>
            <button
              type="button"
              className="btn btn-alt-primary btn-alt-primary--red"
              onClick={handleSave}
            >
              <span className="btn__label">Guardar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const HighlightPopup: React.FC<PopupProps> = (props) =>
  typeof document === "undefined"
    ? null
    : ReactDOM.createPortal(<PopupContent {...props} />, document.body);

export default HighlightPopup;
